import { POSBaseInfoContext } from '@/components/LayoutPOS/Synchronizer';
import { LongTouchAction } from '@/components/LongTouchAction';
import { message } from '@/components/Message';
import Modal from '@/components/Modal';
import PosButton from '@/components/PosButton';
import UserIntro from '@/components/User/UserIntro';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { AppointmentChannel, AppointmentStatus } from '@/models/appointment';
import { DateTimeFormat } from '@/models/common';
import { isServicePreOrder, PreOrder, setOperatorForPreOrder } from '@/models/order';
import { deleteService, getIsPaidOrder, useSyncFromServer } from '@/models/preOrder';
import { IS_POS } from '@/utils/env';
import { playSound } from '@/utils/sound';
import {
  ExclamationCircleOutlined,
  PullRequestOutlined,
  ReloadOutlined,
  RollbackOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { Empty, List, Space, Typography } from 'antd';
import { APIError } from 'api/types';
import dayjs from 'dayjs';
import { produce } from 'immer';
import React, { ComponentProps, Ref, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { AppointmentTimelinePopup } from '@/components/AppointmentTimeline/Timeline';
import { ConfirmModal } from './ConfirmModal';
import { ServiceTimer } from './ServiceTimer';
import { useStatinoStyles, useStationCoreStyles } from './styles';
import { useOrderHistory } from './useOrderHistory';

interface Props {
  history: Record<string, PreOrder>;
  onRequestDelete: (preOrder: PreOrder) => void;
  onRequestPop: (preOrder: PreOrder) => void;
  onRequestChangeOperator: (preOrder: PreOrder) => void;
  onPreOrderChange: (preOrder: PreOrder) => void;
  readonly?: boolean;
}

export const StationCore: React.FC<Props> = ({
  history,
  onRequestDelete,
  onRequestPop,
  onRequestChangeOperator,
  onPreOrderChange,
  readonly = false,
}: Props) => {
  const { styles } = useStationCoreStyles();
  // 预约、服务时长
  const [confirmInfo, setConfirmInfo] = useState<ComponentProps<typeof ConfirmModal>['confirmInfo']>();

  return (
    <>
      {confirmInfo ? (
        <ConfirmModal
          confirmInfo={confirmInfo}
          onClose={() => {
            setConfirmInfo(null);
          }}
          onConfirmed={(appointment, preOrder, userFullInfo, baseTime) => {
            setConfirmInfo(null);
            onPreOrderChange(
              produce(preOrder, draft => {
                draft.appointment = appointment;
                draft.userFullInfo = userFullInfo;
                draft.addTime = baseTime.format(DateTimeFormat);
              })
            );
          }}
        />
      ) : null}
      {!Object.entries(history).length ? (
        <Empty
          className="mt-m"
          image={Empty.PRESENTED_IMAGE_DEFAULT}
          description={
            readonly ? (
              <Typography.Paragraph>当前无服务</Typography.Paragraph>
            ) : (
              <Typography.Paragraph>
                点击<Typography.Text strong>「创建服务」</Typography.Text>
                <br />
                以添加服务单到此处
              </Typography.Paragraph>
            )
          }
        />
      ) : null}
      <>
        {Object.entries(history)
          .reverse()
          .map(([_, preOrder], index) => {
            // const preOrder = history[index];
            const isPaidOrder = getIsPaidOrder(preOrder);
            const user = preOrder.userFullInfo?.user;
            const isService = isServicePreOrder(preOrder);
            return (
              <LongTouchAction
                key={`${preOrder.key}`}
                className={styles.item}
                actions={
                  !readonly && (
                    <>
                      <PosButton
                        danger
                        type="primary"
                        shape="round"
                        onClick={e => {
                          e.stopPropagation();
                          onRequestDelete(preOrder);
                        }}>
                        <RollbackOutlined /> 撤单
                      </PosButton>
                    </>
                  )
                }>
                <>
                  <span className={styles.itemNo}>{Object.keys(history).length - index}</span>
                  <div>
                    {isService ? (
                      <ServiceTimer preOrder={preOrder} />
                    ) : (
                      <span>{dayjs(preOrder.addTime).locale(dayjs.locale('zh-cn')).fromNow()}</span>
                    )}
                  </div>

                  {user ? (
                    <div className="mt-s">
                      <UserIntro
                        showFromUser={false}
                        showGender={false}
                        showRemarkName={false}
                        user={user}
                        avatarProps={{ size: 18 }}
                        spaceProps={{ direction: 'horizontal', size: 1 }}
                      />
                    </div>
                  ) : null}

                  <List
                    pagination={false}
                    size="small"
                    dataSource={preOrder.specifications}
                    renderItem={specification => {
                      return (
                        <List.Item style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <>{specification.goodsName}</>
                          <>{specification.name ?? ''}</>
                          <> X {specification.number}</>
                        </List.Item>
                      );
                    }}
                  />

                  <Space direction="horizontal">
                    {isService ? (
                      preOrder.addTime ? (
                        <></>
                      ) : (
                        !readonly && (
                          <AppointmentTimelinePopup
                            selectMode
                            disabled={Boolean(preOrder.appointment)}
                            filter={appointment =>
                              appointment.channel !== AppointmentChannel.Placeholder &&
                              appointment.status === AppointmentStatus.CONFIRMED
                            }
                            onChange={appointment => {
                              if (appointment === null) {
                                // 未预约，创建占位
                                setConfirmInfo({
                                  preOrder,
                                });
                              } else {
                                // 已预约，流转状态
                                setConfirmInfo({
                                  appointment,
                                  preOrder,
                                });
                              }
                            }}>
                            <PosButton
                              key={'appointment'}
                              type="secondary"
                              onClick={() => {
                                // 外部传入的时候， preOrder 已经携带了预约信息，此处无需选择
                                if (preOrder.appointment) {
                                  setConfirmInfo({
                                    appointment: preOrder.appointment,
                                    preOrder,
                                  });
                                }
                              }}>
                              开始服务
                            </PosButton>
                          </AppointmentTimelinePopup>
                        )
                      )
                    ) : null}

                    {isService ? (
                      <PosButton
                        key="operator"
                        onClick={() => {
                          if (!readonly) {
                            onRequestChangeOperator(preOrder);
                          }
                        }}>
                        {preOrder.operator ? <>{preOrder.operator.nickname}</> : <>美容师</>}
                      </PosButton>
                    ) : null}

                    {!readonly && (
                      <PosButton
                        key={'view'}
                        onClick={() => {
                          onRequestPop(preOrder);
                        }}>
                        <PullRequestOutlined /> {isPaidOrder ? '查看' : '结账'}
                      </PosButton>
                    )}
                  </Space>
                </>
              </LongTouchAction>
            );
          })}
      </>
    </>
  );
};

export type StationRef = {
  deletePreOrder: (preOrder: PreOrder) => Promise<void>;
  push: (preOrder: PreOrder) => Promise<PreOrder>;
} & Omit<ReturnType<typeof useOrderHistory>, 'push'>;

type Prop = {
  onPop: (item: PreOrder) => void;
  onRequestDelete: (item: PreOrder) => void;
  onStationChange: () => void;
  showOverrideWarning: boolean;
  readonly?: boolean;
  title?: React.ReactNode;
};
export const Station = React.forwardRef(
  (
    {
      onPop,
      onRequestDelete,
      showOverrideWarning,
      onStationChange,
      readonly = false,
      title = (
        <>
          <TableOutlined /> 服务台
        </>
      ),
    }: Prop,
    ref: Ref<StationRef>
  ) => {
    const { styles } = useStatinoStyles();
    const [, , { showSelectOperatorModal }] = useContext(BaseInfoContext);
    const { getOperatorList } = useContext(POSBaseInfoContext);
    const { history, push, pop, popByOrderSn, empty, size, updateItem } = useOrderHistory(readonly);
    const {
      payload: remoteHistory = {},
      send: fetchRemoteHistory,
      loading: remoteHistoryLoading,
    } = useSyncFromServer();

    // readonly 模式从远程获取数据
    useEffect(() => {
      if (readonly) {
        fetchRemoteHistory();
        // const timer = setInterval(runner, 3000);
        // return () => {
        //   console.log('clear');
        //   clearInterval(timer);
        // };
      }
    }, []);

    useEffect(() => {
      onStationChange();
    }, [size]);

    const pushTo = async (preOrder: PreOrder) =>
      new Promise<PreOrder>(resolve => {
        const done = (order: PreOrder) => {
          push(order);
          resolve(order);
        };
        done(preOrder);
      });

    const deletePreOrder = async (preOrder: PreOrder) =>
      new Promise<void>((resolve, reject) => {
        Modal.confirm({
          title: '撤单二次确认',
          content: getIsPaidOrder(preOrder) ? '订单当前正在服务中，确认要撤单吗？' : '是否确认撤单',
          onOk: async () => {
            // 从记录中移除
            pop(preOrder);

            // 已经开始的服务单，修改预约与订单
            if (preOrder.addTime && (preOrder.appointment || preOrder.orderSn)) {
              try {
                await deleteService({ id: preOrder.appointment.id, orderSn: preOrder.orderSn });
                resolve();
              } catch (e) {
                Modal.error({
                  title: '撤单失败',
                  content: `原因: ${(e as APIError).msg}, 请联系店长处理`,
                });
                reject();
              }
            }
          },
        });
      });

    useImperativeHandle(ref, () => {
      return {
        deletePreOrder,
        history,
        push: pushTo,
        pop,
        popByOrderSn,
        empty,
        updateItem,
        // getSize: () => size,
        size,
      };
    }, [history, push, pop, empty, size]);

    const records = readonly ? remoteHistory : history;

    return (
      <div className={`${styles.container} ${IS_POS ? styles.pos : ''}`}>
        <h3 className={styles.head}>
          <div className={styles.headCenter}>{title}</div>
          <div className={styles.headActions}>
            {readonly && (
              <ReloadOutlined
                spin={remoteHistoryLoading}
                onClick={async () => {
                  if (remoteHistoryLoading) return;
                  await fetchRemoteHistory();
                  message.success('更新成功');
                }}
              />
            )}
          </div>
        </h3>
        <div className={styles.center}>
          <>
            <StationCore
              readonly={readonly}
              history={records}
              onPreOrderChange={preOrder => {
                updateItem(preOrder);
              }}
              onRequestChangeOperator={preOrder => {
                // 变更操作员
                showSelectOperatorModal({
                  visible: true,
                  operatorList: getOperatorList(),
                  value: preOrder.operator,
                  onChange: async staff => {
                    playSound('click_light');
                    const order = await setOperatorForPreOrder(preOrder, staff);
                    updateItem(order);
                    message.success('美容师已变更');
                  },
                  onCancel: () => {
                    playSound('click');
                  },
                });
              }}
              onRequestDelete={preOrder => {
                onRequestDelete(preOrder);
              }}
              onRequestPop={preOrder => {
                const isPaidOrder = getIsPaidOrder(preOrder);

                const confirmPop = () => {
                  if (isPaidOrder) {
                    // 有 ordersn 的订单，只查看不移出
                    onPop(preOrder);
                  } else {
                    pop(preOrder);
                    onPop(preOrder);
                  }
                };

                if (showOverrideWarning && !isPaidOrder) {
                  Modal.confirm({
                    autoFocusButton: 'ok',
                    cancelButtonProps: { size: 'large' },
                    okButtonProps: { size: 'large' },
                    title: '取单',
                    icon: <ExclamationCircleOutlined />,
                    content: <>待结清单中存在已添加的服务或商品，取出的数据将覆盖当前数据，是否继续?</>,
                    onOk() {
                      confirmPop();
                    },
                  });
                } else {
                  confirmPop();
                }
              }}
            />
          </>
        </div>
      </div>
    );
  }
);
